<template>
  <div class="app-root">
    <Web3AuthProvider :config="web3AuthContextConfig">
      <WagmiProvider>
        <Suspense>
          <template #default>
            <transition name="page-fade" mode="out-in">
              <router-view v-slot="{ Component }">
                <component :is="Component" />
              </router-view>
            </transition>
          </template>
          <template #fallback>
            <div class="global-loading">Loading...</div>
          </template>
        </Suspense>
      </WagmiProvider>
    </Web3AuthProvider>

    <el-dialog
      v-model="showMaintenance"
      title=""
      width="90%"
      align-center
      :show-close="false"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      class="quant-maintenance-dialog light-theme"
      :modal-class="'quant-backdrop'"
    >
      <div class="dialog-content">
        <div class="icon-wrapper">
          <span class="pulse-icon">🤖</span>
        </div>
        <h2 class="maintenance-title">QuantBot Upgrade</h2>
        <p class="maintenance-desc">
          We are currently optimizing the QuantBot engine to provide better execution strategies and
          higher stability.
        </p>
        <p class="maintenance-sub">
          Please check back later for the new version with enhanced trading experience.
        </p>
      </div>
      <template #footer>
        <div class="dialog-footer">
          <el-button class="go-back-btn" @click="handleGoBack"> Go Back </el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
  import { ref, watch } from 'vue'
  import { useRouter, useRoute } from 'vue-router'
  import { Web3AuthProvider } from '@web3auth/modal/vue'
  import { WagmiProvider } from '@web3auth/modal/vue/wagmi'
  import web3AuthContextConfig from './utils/web3authContext'

  // --- 配置区域 ---
  // 【开关】设置为 true 开启拦截，设置为 false 关闭拦截
  const ENABLE_QUANTBOT_MAINTENANCE = false

  // --- 逻辑区域 ---
  const router = useRouter()
  const route = useRoute()
  const showMaintenance = ref(false)

  // 监听路由变化
  watch(
    () => route.path,
    (newPath) => {
      // 1. 检查开关是否开启
      // 2. 检查路径是否包含 '/quantbot' (不区分大小写)
      if (ENABLE_QUANTBOT_MAINTENANCE && newPath && newPath.toLowerCase().includes('/quantbot')) {
        showMaintenance.value = true
      } else {
        // 如果离开了 quantbot 路径，自动关闭弹窗（防止状态残留）
        showMaintenance.value = false
      }
    },
    { immediate: true } // 立即执行一次，防止用户直接刷新在 quantbot 页面时不触发
  )

  const handleGoBack = () => {
    // 尝试后退，如果没有历史记录则跳回首页
    if (window.history.length > 1) {
      router.back()
    } else {
      router.push('/')
    }
  }
</script>

<style scoped lang="scss">
  /* 这里复用 main.vue 中的基础变量 
  注意：如果 variables.scss 在不同目录，请调整路径
*/
  @use '@/styles/variables.scss' as *;

  /* 保持原有的 App.vue 布局样式 */
  .app-root {
    max-width: 1248px;
    margin: 0 auto;
  }

  .global-loading {
    min-height: calc(100vh - 3.6rem);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #9fcad1;
    font-size: 0.95rem;
  }

  .page-fade-enter-active,
  .page-fade-leave-active {
    transition: opacity 240ms ease, transform 240ms ease;
  }
  .page-fade-enter-from,
  .page-fade-leave-to {
    opacity: 0;
    transform: translateY(6px);
  }

  :deep(.quant-maintenance-dialog.light-theme) {
    /* 使用纯白或极浅的灰色背景 */
    background: #ffffff;
    border: 1px solid rgba(0, 0, 0, 0.05);
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
    border-radius: 20px;
    max-width: 420px;

    .el-dialog__header {
      display: none;
    }

    .el-dialog__body {
      padding: 40px 24px 10px 24px;
      text-align: center;
      /* 主文字改为深色 */
      color: #1e293b;
    }

    .el-dialog__footer {
      padding: 10px 24px 40px 24px;
      border-top: none;
      text-align: center;
    }
  }

  /* 遮罩层改用浅色半透明，增加磨砂感 */
  :deep(.quant-backdrop) {
    background-color: rgba(255, 255, 255, 0.6);
    backdrop-filter: blur(4px);
  }

  /* 内容样式调整 */
  .icon-wrapper {
    font-size: 3.5rem;
    margin-bottom: 20px;
    /* 给图标一点阴影感 */
    filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1));
  }

  .maintenance-title {
    font-size: 1.5rem;
    font-weight: 800;
    margin: 0 0 12px 0;
    /* 使用 main.vue 中常见的深色文字 */
    color: #0f172a;
    letter-spacing: -0.5px;
  }

  .maintenance-desc {
    font-size: 1rem;
    /* 柔和的深灰色描述 */
    color: #475569;
    line-height: 1.6;
    margin-bottom: 12px;
  }

  .maintenance-sub {
    font-size: 0.85rem;
    /* 对应 main.vue .stat-sub 的浅灰色调 */
    color: #94a3b8;
    margin-bottom: 24px;
  }

  /* 按钮样式 - 保持绿色调但调整为更明亮的浅色适配版 */
  .go-back-btn {
    background: #10b981;
    border: none;
    color: white;
    font-weight: 600;
    padding: 12px 48px;
    height: auto;
    border-radius: 12px;
    font-size: 1rem;
    transition: all 0.2s ease;
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.2);

    &:hover {
      background: #0d9488;
      transform: translateY(-1px);
      box-shadow: 0 6px 20px rgba(16, 185, 129, 0.3);
    }

    &:active {
      transform: translateY(0);
    }
  }

  /* 脉冲动画保持不变 */
  @keyframes pulse-animation {
    0% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.05);
    }
    100% {
      transform: scale(1);
    }
  }
  .pulse-icon {
    display: inline-block;
    animation: pulse-animation 2s infinite ease-in-out;
  }
</style>
